import { Palette } from '@mui/material';
import { alpha } from '@mui/material/styles';
import chroma from 'chroma-js';

import { ThemeConfiguration } from 'shared/components/utils/Theme';

export const getLegacyCybertronTheme = (
  palette: Palette
): ThemeConfiguration => {
  const {
    background,
    border,
    chart,
    common,
    error,
    grey,
    icon,
    info,
    link,
    primary,
    status,
    success,
    text,
    warning,
  } = palette;

  return {
    name: 'cybertron',
    variables: {
      '--color-common-white': common.white,
      '--color-grey-500': grey[500],
      '--color-grey-700': grey[700],
      '--color-primary-main': primary.main,
      '--color-primary-light': primary.light,
      '--color-text-primary': text.primary,
      '--color-text-secondary': text.secondary,
      '--color-text-error': text.error,
      '--color-text-contrast': text.contrast,
      '--color-error-light': error.light,
      '--color-error-main': error.main,
      '--color-error-dark': error.dark,
      '--color-error-contrast-text': error.contrastText,
      '--color-warning-light': warning.light,
      '--color-warning-main': warning.main,
      '--color-icon': icon,
      '--color-info-contrast-text': info.contrastText,
      '--color-info-light': info.light,
      '--color-info-main': info.main,
      '--color-info-dark': info.dark,
      '--color-success-main': success.main,
      '--color-background-paper-hover': background.paperHover,
      '--color-background-paper': background.paper,
      '--color-background-default': background.default,
      '--color-background-chip': background.chip,
      '--color-background-contrast': background.contrast,
      '--color-link-main': link.main,
      '--color-link-hover': link.hover,
      '--color-link-visited': link.visited,
      '--color-status-critical': status.critical,
      '--color-status-high': status.high,
      '--color-status-medium': status.medium,
      '--color-status-low': status.low,
      '--color-chart-blue': chart.blue,
      '--color-chart-blue-light': chart.blueLight,
      '--color-chart-purple': chart.purple,
      '--color-chart-purple-dark': chart.purpleDark,
      '--color-chart-turquoise': chart.turquoise,
      '--color-chart-green': chart.green,
      '--color-chart-yellow-dark': chart.yellowDark,
      '--color-chart-red': chart.red,
      '--color-border': border,
      '--color-chart-grey': chart.grey,

      /**
       * HUD
       */
      '--color-hud-gradient': alpha(primary.main, 0.3),

      /**
       * HUD lower bar on Created Insights and unfilled label meter bar
       */
      '--color-error-dark-half-opacity': alpha(error.dark, 0.5),

      /**
       * Layered elements that lay on top of background and
       * surface such as tooltips and notifications.
       * Used by Bridge
       */
      '--color-layer-transparent': chroma(background.default)
        .darken(0.8)
        .alpha(0.9)
        .css(),

      /**
       * Content Drawer
       */
      '--color-background-default-half-alpha': chroma(background.default)
        .alpha(0.5)
        .css(),

      /**
       * Used by reaviz
       */
      '--color-background': background.default,
      '--color-primary': primary.main,
      '--color-handle-fill': background.default,
      '--color-handle-stroke': primary.main,
      '--color-handle-drag-fill': background.paper,
      '--color-handle-dots': icon,
      '--color-handle-line': primary.main,
    },
    palettes: {
      /**
       * Identicon base colors
       */
      identicon: [
        chart.purple,
        chart.turquoise,
        chart.yellowDark,
        chart.blue,
        chart.green,
      ],

      /**
       * Vendor and product metric colors
       */
      vendorProductMetrics: [
        chart.blueLight,
        chart.blue,
        chart.purpleDark,
        chart.purple,
      ],
    },
    gradients: {
      insightScatterPlotAxis: [
        {
          offset: '1%',
          stopOpacity: 0.9,
          color: chart.greyDark,
        },
        {
          offset: '30%',
          stopOpacity: 1,
          color: chart.grey,
        },
        {
          offset: '50%',
          stopOpacity: 1,
          color: chart.yellow,
        },
        {
          offset: '60%',
          stopOpacity: 1,
          color: chart.orange,
        },
        {
          offset: '80%',
          stopOpacity: 1,
          color: chart.red,
        },
      ],
    },
  };
};
